/**
 * Public OAuth client ids the Turnkey provider needs explicitly.
 *
 * A Turnkey identity is `iss + aud + sub`, and `aud` is the client id, so the
 * SAME person signing in with two different client ids lands in two different
 * sub-organizations — with their wallets in only one of them. Web and mobile
 * each carried their own copy of this value and drifted apart, which split
 * real accounts and locked people out of their own wallets. One exported
 * constant is the fix: there is nothing left to drift.
 *
 * Do not reintroduce a per-platform env var for these. If an environment ever
 * needs a different OAuth application, make that explicit here rather than in
 * a build-time variable that only one platform reads.
 */

/**
 * Real X (Twitter) OAuth 2.0 client id.
 *
 * Passed explicitly because the auth proxy's `oauthClientIds.x` returns the
 * Turnkey credential id (a UUID) instead of this value, which x.com rejects on
 * the authorize request. Public, non-secret, and network-independent — the
 * Turnkey organization is a singleton across devnet and mainnet. Drop this once
 * the proxy publishes the credential's stored client id.
 */
import { decodeJwtPayload } from '../oauth/oauth-claims.js';

export const TURNKEY_X_CLIENT_ID = 'OVR0RDV6YjhVSEk2RWVIakJRU3c6MTpjaQ';

/**
 * Apple audience of the native iOS sign-in: the app's bundle identifier
 * (`ios.bundleIdentifier` in `mobile/app.json`).
 *
 * Web signs in with the Apple Services ID, a different audience. Web passes
 * this value as a secondary Apple client id so a sub-org created on web also
 * accepts the iOS token; the iOS kit already registers the Services ID the
 * same way.
 */
export const TURNKEY_APPLE_IOS_CLIENT_ID = 'com.myprotocol.morfi';

/**
 * Apple audience of web and Android sign-in: the Apple Services ID (the Apple
 * client id in the Turnkey dashboard and mobile `EXPO_PUBLIC_APPLE_CLIENT_ID`).
 */
export const TURNKEY_APPLE_SERVICES_ID = 'com.myprotocol.morfi.signin';

/**
 * The Apple audiences other than the token's own, as Turnkey OIDC claims.
 * Connecting Apple registers only the audience of the token in hand; adding
 * these keeps the same Apple ID signing in to this sub-org from the other
 * platform too, instead of creating a second sub-org there.
 */
export function appleSecondaryOidcClaims(
  oidcToken: string,
): Array<{ iss: string; sub: string; aud: string }> {
  const claims = decodeJwtPayload(oidcToken);
  const { iss, sub, aud } = claims;
  if (typeof iss !== 'string' || typeof sub !== 'string') return [];
  const tokenAudiences = new Set(Array.isArray(aud) ? aud : [aud]);
  return [TURNKEY_APPLE_SERVICES_ID, TURNKEY_APPLE_IOS_CLIENT_ID]
    .filter((audience) => !tokenAudiences.has(audience))
    .map((audience) => ({ iss, sub, aud: audience }));
}
